import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactModal from "react-modal";

import { EditUserButton, PdfShower } from "../components";
import { MobileTopbar } from "../components/layout";
import { UserSearchSelect } from "../components/inputs";
import { userOptionName } from "../components/inputs/UserSearchSelect";
import useUserUpdates from "../hooks/useUserUpdates";
import ContactWizard from "../views/Users/Wizard.jsx";
import { contactWizardModalStyles } from "../core/utils/responsiveModalStyles.js";
import { UserContext } from "../contexts";
import { getFileUrl } from "../core/config/api.js";
import { searchSellRequests } from "./intakeApi";
import { ownedByOptions } from "../listings/constants";
import { SectionSavedBadge } from "../forms/autoSaveStatus";
import SectionTrigger from "../forms/SectionTrigger.jsx";
import { Button, Chip, Icon, IconButton, SearchInput, Select, Toggle } from "../components/ui";
import "./rc-listing-flows.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────

export const IDoc = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4 2h5l3 3v9H4V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M9 2v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);
export const IUserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M3.5 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
export const IFlag = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M4 14V2.5M4 3h8l-1.8 2.5L12 8H4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
// Watch placeholder for a product row with no image — a case + lugs + hands,
// so an imageless row still reads as "a watch" rather than "a document".
const IWatch = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="5.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 6.5L10 3.5h4l.5 3M9.5 17.5l.5 3h4l.5-3"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 9.5V12l1.8 1.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// ─── Section shell ────────────────────────────────────────────────────────────
// Visual twin of RCListingForm's RCSection (same rc-lp__section shell and the
// same SectionTrigger), but usable OUTSIDE the form — the flow pages own the
// open/close state of their intake sections.
// `summary` is shown on the trigger while the section is CLOSED. These sections
// collapse the moment the rep commits with Continue, so without it the choices
// they just made scroll out of sight — the summary keeps "which source did I
// pick, and is this a repair" answerable at a glance, and the trigger reopens
// the section to change either.
export const FlowSection = ({
  icon,
  label,
  isOpen,
  onToggle,
  children,
  summary = null,
}) => (
  <div className="rc-lp__section">
    <SectionTrigger
      icon={icon}
      label={label}
      isOpen={isOpen}
      onToggle={onToggle}
      summary={!isOpen && summary ? <span className="rc-flow__section-summary">{summary}</span> : null}
    />
    {isOpen && (
      <div className="rc-lp__section-body">
        {children}
        {/* Auto-save confirmation — only renders where RCListingForm provides
            the status context (the edit flow). Intake edits persist through
            the same form auto-save (enforce/clear write into Formik). */}
        <SectionSavedBadge />
      </div>
    )}
  </div>
);

// ─── New client/consignor wizard modal ────────────────────────────────────────
// Same shell as SupplierWizardModal but with a configurable contact type —
// sellers/consignors are clients, not suppliers.
export const NewContactModal = ({ isOpen, onClose, afterSubmit, title, submitLabel, defaultType = "client" }) => (
  <ReactModal
    isOpen={isOpen}
    onRequestClose={onClose}
    style={contactWizardModalStyles}
    className="rc-supplier-wizard-modal"
    overlayClassName="rc-supplier-wizard-modal-overlay"
    contentLabel={title}
  >
    <div className="users-dark users-dark--modal users-dark--create rc-supplier-wizard-modal__shell">
      <MobileTopbar />
      <div className="users-create-wrap users-create-wrap--modal">
        <div className="users-create-wrap__header">
          <Button
            variant="ghost"
            data-btn="contact-cancel"
            leadingIcon={<Icon name="ArrowLeft" size="sm" />}
            onClick={onClose}
          >
            Back
          </Button>
          <h2 className="users-create-wrap__title">{title}</h2>
        </div>
        <ContactWizard
          className="users-create-form"
          afterSubmit={afterSubmit}
          onCancel={onClose}
          submitLabel={submitLabel}
          defaultType={defaultType}
        />
      </div>
    </div>
  </ReactModal>
);

// ─── Assign Seller / Assign Consignor picker ──────────────────────────────────
// Suggestion chips (from Gemini analysis or the selected Sell Request) +
// full store-wide user search + "new user" creation. `value` is {id, name}.
export const AssignPersonPicker = ({
  label,
  suggestions = [],
  suggestionsNote,
  value,
  onChange,
  createTitle = "New Client",
  // Seeds the search box. Request dictation passes the client NAME it heard, so
  // the rep lands on matches for it instead of retyping what they just said.
  // Optional and undefined by default, so every existing caller is unchanged.
  defaultInputValue,
}) => {
  const [isCreating, setIsCreating] = useState(false);

  const personName = (u) =>
    u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || `User #${u?.id}`;

  const onCreated = (created) => {
    setIsCreating(false);
    if (created?.id) onChange({ id: created.id, name: personName(created), type: created.type });
  };

  // The assigned person is held here as {id, name} only, so a name / type /
  // store edited in the edit tab has to be re-read and the label rebuilt.
  const onPersonRefreshed = useCallback(
    (fresh) => onChange({ id: fresh.id, name: userOptionName(fresh), type: fresh.type }),
    [onChange]
  );
  useUserUpdates(value?.id, onPersonRefreshed, { refreshOnFocus: true });

  return (
    <div className="rc-flow__assign">
      {/* Optional: a caller that already heads its own section passes none,
          rather than showing the same word twice one line apart. */}
      {label && <div className="rc-field__label">{label}</div>}

      {suggestions.length > 0 && (
        <div className="rc-flow__chips">
          {suggestions.map((u) => (
            <Chip
              key={u.id}
              icon={<Icon name="User" size="sm" />}
              selected={value?.id === u.id}
              data-btn="assign-confirm"
              onClick={() => onChange({ id: u.id, name: personName(u) })}
              title={[u.email, u.phone].filter(Boolean).join(" · ")}
            >
              {personName(u)}
            </Chip>
          ))}
        </div>
      )}
      {suggestionsNote && <div className="rc-flow__hint">{suggestionsNote}</div>}

      <div className="rc-flow__assign-controls">
        <UserSearchSelect
          className="select-container rc-flow__user-select"
          placeholder={`Search all users in your store...`}
          defaultInputValue={defaultInputValue}
          value={value || null}
          defaultOptions={value ? [value] : true}
          editable
          onChange={(option) =>
            option && onChange({ id: option.id, name: option.name, type: option.type })
          }
        />
        <Button variant="secondary" size="lg" data-btn="assign-new" onClick={() => setIsCreating(true)}>
          New User
        </Button>
      </div>

      {value && (
        <div className="rc-flow__assigned">
          Assigned: <strong>{value.name}</strong>
          <EditUserButton user={value} className="rc-flow__assigned-edit" />
          <IconButton
            variant="ghost"
            size="sm"
            label="Clear"
            data-btn="assign-cancel"
            onClick={() => onChange(null)}
          >
            <Icon name="X" size="sm" />
          </IconButton>
        </div>
      )}

      <NewContactModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        afterSubmit={onCreated}
        title={createTitle}
        submitLabel="Create user"
      />
    </div>
  );
};

// ─── Store-wide Sell Request search ───────────────────────────────────────────
// The house brands reps reach for most — one tap AND-narrows a request
// search to that maker (the backend treats each search word as AND, so a
// chip composes with anything already typed). Not an exhaustive list; the
// text box still searches every brand.
export const REQUEST_BRAND_CHIPS = ["Rolex", "Audemars Piguet", "Patek Philippe"];

// Chip, not Segmented: a brand filter can be switched OFF again (tap the
// active chip), which an exclusive segmented control cannot express.
export const BrandChips = ({ active, onToggle }) => (
  <div className="rc-flow__search-chips">
    {REQUEST_BRAND_CHIPS.map((b) => (
      <Chip
        key={b}
        selected={active === b}
        data-btn="request-filter"
        onClick={() => onToggle(active === b ? null : b)}
      >
        {b}
      </Chip>
    ))}
  </div>
);

// Searches every sell request in the active store by client name / store /
// company / brand / model / reference (admin path of POST
// /api/user/{me}/requests/sell). A brand chip AND-narrows on the maker.
export const SellRequestSearch = ({ selected, onSelect, onClear }) => {
  const { user } = useContext(UserContext);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);
  // Chip + free text combine into one AND search term.
  const term = [brand, query].filter(Boolean).join(" ").trim();

  useEffect(() => {
    if (!user?.id || selected) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        setItems(await searchSellRequests(user.id, term));
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
        setTouched(true);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [term, user?.id, selected]);

  if (selected) {
    const product = selected.product || {};
    const client = selected.client || {};
    return (
      <div className="rc-flow__request-selected">
        <div className="rc-flow__request-main">
          <div className="rc-flow__request-meta">
            {[product.reference_number, product.brand?.name].filter(Boolean).join(" • ")}
          </div>
          <div className="rc-flow__request-name">{product.model?.name || `Request #${selected.id}`}</div>
          <div className="rc-flow__request-sub">
            {[
              [client.first_name, client.last_name].filter(Boolean).join(" "),
              selected.status,
              `#${selected.id}`,
            ].filter(Boolean).join(" / ")}
          </div>
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          label="Clear selection"
          data-btn="request-cancel"
          onClick={onClear}
        >
          <Icon name="X" size="sm" />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="rc-flow__request-search">
      <SearchInput
        size="md"
        value={query}
        placeholder="Search by client, store, company, brand, model or reference..."
        onChange={(e) => setQuery(e.target.value)}
        onClear={() => setQuery("")}
      />

      <BrandChips active={brand} onToggle={setBrand} />

      {loading && <div className="rc-flow__hint">Searching sell requests…</div>}
      {!loading && touched && items.length === 0 && (
        <div className="rc-flow__hint">No sell requests match.</div>
      )}
      {!loading && items.length > 0 && (
        <div className="rc-flow__request-list">
          {items.map((req) => {
            const product = req.product || {};
            const client = req.client || {};
            const img = product.images?.[0]?.id ? getFileUrl(product.images[0].id) : null;
            const meta = [product.reference_number, product.brand?.name, product.model?.name].filter(Boolean).join(" • ");
            return (
              <div key={req.id} className="rc-flow__request-row">
                <span className="rc-flow__request-thumb">
                  {img ? <img src={img} alt="" /> : <IWatch />}
                </span>
                <span className="rc-flow__request-info">
                  <span className="rc-flow__request-meta">{meta}</span>
                  <span className="rc-flow__request-sub">
                    {[
                      [client.first_name, client.last_name].filter(Boolean).join(" "),
                      req.status,
                      `#${req.id}`,
                    ].filter(Boolean).join(" / ")}
                  </span>
                </span>
                {/* The visible word stays "Select"; the name adds which request,
                    since every row has one. */}
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label={`Select ${meta || `request #${req.id}`}`}
                  data-btn="request-confirm"
                  onClick={() => onSelect(req)}
                >
                  Select
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Linked Sell Request slot (replaces "Choose Supplier" in flow mode) ───────
// Internal Details on the purpose-built flows shows the LINKED Sell Request
// instead of the raw supplier picker: the selected request as a card, or the
// choice between selecting an existing one and creating one.
//
// "Create Sell Request" CREATES IT NOW and shows it as linked. It used to only
// arm a flag that the save handler acted on much later, which read as a button
// that did nothing. `onCreate` is therefore async, and the two things it needs —
// a person and a product — are reported through `createBlockedNote` so the
// button can say why it is unavailable instead of failing on click.
//
// `readOnly` reports the link without offering to change it. Used where the
// caller has no way to persist a change.
export const LinkedRequestSlot = ({
  selected,
  onSelect,
  onClear,
  onCreate,
  createBlockedNote,
  readOnly = false,
  emptyNote,
}) => {
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const create = async () => {
    if (creating || createBlockedNote) return;
    setSearching(false);
    setError(null);
    setCreating(true);
    try {
      await onCreate?.();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "The Sell Request could not be created — try again."
      );
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    const product = selected.product || {};
    const client = selected.client || {};
    return (
      <div className="rc-flow__linked-request">
        <div className="rc-field__label">Linked Sell Request</div>
        <div className="rc-flow__request-selected">
          <div className="rc-flow__request-main">
            <div className="rc-flow__request-meta">
              {[product.reference_number, product.brand?.name].filter(Boolean).join(" • ")}
            </div>
            <div className="rc-flow__request-name">
              {product.model?.name || `Request #${selected.id}`}
            </div>
            <div className="rc-flow__request-sub">
              {[
                [client.first_name, client.last_name].filter(Boolean).join(" "),
                selected.status,
                `#${selected.id}`,
              ].filter(Boolean).join(" / ")}
            </div>
          </div>
          {!readOnly && (
            <IconButton
              variant="ghost"
              size="sm"
              label="Unlink request"
              data-btn="request-delete"
              onClick={onClear}
            >
              <Icon name="X" size="sm" />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="rc-flow__linked-request">
        <div className="rc-field__label">Connect Supplier Info with the Listing</div>
        <div className="rc-flow__hint">
          {emptyNote ||
            "No Sell Request linked to this listing."}
        </div>
      </div>
    );
  }

  return (
    <div className="rc-flow__linked-request">
      <div className="rc-field__label">Connect Supplier Info with the Listing</div>
      <div className="rc-flow__linked-actions">
        <Button
          variant="secondary"
          size="sm"
          active={searching}
          aria-pressed={searching}
          data-btn="request-search"
          onClick={() => { setSearching((s) => !s); setError(null); }}
        >
          Select Existing Request
        </Button>
        {onCreate && (
          <>
            <span className="rc-flow__source-or">or</span>
            <Button
              variant="secondary"
              size="sm"
              data-btn="request-create"
              onClick={create}
              disabled={creating || !!createBlockedNote}
              title={createBlockedNote || undefined}
            >
              {creating ? "Creating…" : "New Sell Request"}
            </Button>
          </>
        )}
      </div>
      {searching && (
        <SellRequestSearch
          selected={null}
          onSelect={(req) => { setSearching(false); onSelect(req); }}
          onClear={() => {}}
        />
      )}
      {createBlockedNote && <div className="rc-flow__hint">{createBlockedNote}</div>}
      {error && <div className="rc-flow__ai rc-flow__ai--unavailable">{error}</div>}
    </div>
  );
};

// ─── Single-file upload box (pdf / image) ─────────────────────────────────────
export const IntakeFileBox = ({ label, accept, file, onPick, onClear, uploading, hint }) => {
  const inputRef = useRef(null);
  const isPdf = (file?.name || "").toLowerCase().endsWith(".pdf");
  return (
    <div className="rc-flow__filebox">
      <div className="rc-field__label">{label}</div>
      {file ? (
        <div className="rc-flow__filebox-chip">
          {isPdf ? (
            <span className="rc-lp__invoice-pdf">PDF</span>
          ) : (
            <img src={getFileUrl(file.id)} alt={file.name} />
          )}
          <span className="rc-flow__filebox-name">{file.name}</span>
          <IconButton
            variant="ghost"
            size="sm"
            label="Remove file"
            data-btn="intake-delete"
            onClick={onClear}
          >
            <Icon name="X" size="sm" />
          </IconButton>
        </div>
      ) : (
        <Button
          variant="secondary"
          data-btn="intake-add"
          leadingIcon={<Icon name="Upload" size="sm" />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : `Upload ${accept.includes("pdf") ? "PDF" : "image"}`}
        </Button>
      )}
      {hint && <div className="rc-flow__hint">{hint}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

// ─── AI status strip ──────────────────────────────────────────────────────────
// `empty` separates the two SUCCESSFUL outcomes. The backend answers 200 with
// empty structures when it did read the documents and found nothing worth
// suggesting, and 503 (→ `unavailable`) when it could not read them at all.
// Without the distinction, a blank read still claimed "matches applied below".
export const AiStatus = ({ state, empty = false }) => {
  if (!state) return null;
  const text = {
    loading:     "Analysing with our image recognition model…",
    done: empty
      ? "Analysis complete — nothing recognised, fill in manually."
      : "Analysis complete — matches applied below.",
    unavailable:
      "Image recognition unavailable right now — assign and fill in manually.",
  }[state];
  if (!text) return null;
  return <div className={`rc-flow__ai rc-flow__ai--${state}`}>{text}</div>;
};

// ─── Owned By (intake-section copy) ──────────────────────────────────────────
// Presentational twin of the Formik-bound "Owned By" RCSelect in Listings. It
// sits above the In Repair toggle in the UPLOAD (New Stock) / ASSIGN CONSIGNOR
// (New Consignment) intake sections, which render ABOVE and OUTSIDE Formik — so
// it drives page state, and the flow mirrors that into Formik's
// `internal_product` via `enforce` while reporting the Listings-section value
// back via `reportFields`. One value, two controls: the flow seeds the default
// (New Stock → owned, New Consignment → exclusive) and the rep may change it
// from either place. Same ui Select as the form's copy.
export const OwnedByField = ({ value, onChange, storeName }) => {
  const options = ownedByOptions(storeName).map((o) => ({ value: o.id, label: o.name }));
  const selected = options.find((o) => o.value === value);

  return (
    <div className="rc-flow__owned-by">
      <div className="rc-field">
        <div className="rc-field__label">Owned By</div>
        <Select
          size="lg"
          fullWidth
          label="Owned By"
          placeholder="—"
          options={options}
          value={selected ? selected.value : null}
          onChange={(v) => onChange(v)}
        />
      </div>
    </div>
  );
};

// ─── In Repair toggle (intake-section copy) ───────────────────────────────────
// Presentational twin of the Formik-bound InRepairToggle in Internal Details ▸
// Cost. It sits in the UPLOAD (New Stock) / ASSIGN CONSIGNOR (New Consignment)
// intake sections, which render ABOVE and OUTSIDE Formik — so it drives page
// state, and the flow mirrors that into Formik's `is_repair` via `enforce`
// while reporting the Cost-tab value back via `reportFields`. The two toggles
// therefore always read the same value. Replaces the old CREATE REPAIR button.
export const RepairToggleField = ({ value, onChange }) => (
  <div className="rc-flow__repair">
    <Toggle
      label="In Repair"
      checked={!!value}
      data-btn="intake-repair-toggle"
      onChange={(next) => { onChange(next); }}
    />
  </div>
);

export { PdfShower };
